import { createSlice } from '@reduxjs/toolkit'
import HTTP_CODE from 'utils/HTTP_CODE'
import API from './API'
import difference from 'lodash-es/difference'
import { differenceInMinutes } from 'date-fns'
import { Donkey, memoizedThrottle, newDate, notifyOrder, platform, pushWrapper } from 'utils'
import { fetchOrders, getOrderIds, getUpdatedAt } from './Orders'
import { getResellerStyle } from './Reseller'
import Order from './models/Order'

const slice = createSlice({
	name        : 'push',
	initialState: {
		sender    : null,
		token     : null,
		canReceive: false,
	},
	reducers    : {
		setPush: (state, { payload: { token, sender = null, canReceive = null } }) => {
			state.token      = token
			state.sender     = sender === null? state.sender : sender
			state.canReceive = canReceive === null? state.canReceive : canReceive
		},
		togglePush: (state, { payload: toggle }) => { state.canReceive = toggle }
	},
})

//not exactly official, but enough for some organization
slice.thunks = {
	//FIXME a couple of minor stuff:
	// - should push be enabled by default for new logins? if so, how? I guess we could use the trilean from canReceive to identify that?
	// - doesn't seem to turn on for users that had it on after switching accounts (i.e. from reseller with push on to another account and then back to reseller)
	/**
	 * Needs to be called AFTER setting the Reseller state.
	 * @param token
	 * @param sender
	 * @param canReceive
	 * @param hasReseller
	 * @returns {function(...[*]=)}
	 */
	initPush: ({ token, sender, canReceive }) => async (dispatch, getState) => {
		pushWrapper(sender, getResellerStyle(getState())) //initializes the PushNotification object
		dispatch(slice.actions.setPush({ token, sender, canReceive }))
		if (canReceive) {
			dispatch(slice.thunks.pushOn)
		}
	},

	//it's not exported (to be used by PushWrapper) since it's still internal stuff, so must be passed as argument
	registerToken: (token, env = platform()) => async dispatch => {
		await API.post(`user/push/${env}/${token}`)
		return dispatch(slice.actions.setPush({ token, canReceive: true }))
	},

	//this is also called at src/index.js and during Auth, so we're sure it always registers, even if the app is
	// reinstalled / rebuilt, or when the user re-logs in
	pushOn: async (dispatch, getState) => {
		const state = getState()
		const push  = pushWrapper(state.push.sender, getResellerStyle(state), true) //reload makes sure we're not re-registering handlers on the same object, in case of failures

		//FIXME: find a way to receive web pushes; in the meantime, we force-call the API so we can test its behavior
		if (!isPushAllowed()) {
			//TODO: for some reason, if you try to test push changes on the web, it won't trigger an App remount (where pushOn should happen)
			console.error('Should not arrive here as the Push Switch is disabled on the web and simulators...?')
			await dispatch(slice.thunks.registerToken('fake-web-token', 'android'))
		}

		//this callback needs to be defined here as it needs enablePush() - which needs dispatch()
		push.on('registration', pushWrapper.on.registration(slice.thunks.registerToken, slice.thunks.setupDonkeyRefreshes))
		push.on('error', pushWrapper.on.error)
		push.on('notification', memoizedThrottle( //iOS 18: https://app.shortcut.com/ehungry/story/58368/
			pushWrapper.on.notification,
			10_000, //so it can also block doubled resent requests, but not for too long as to hinder testing
			payload => payload.additionalData.notId
		))
	},

	/**
	 * Removes anything that's going to update the app state automatically.
	 * @param dispatch
	 * @returns {Promise<void>}
	 */
	clearPush: dispatch => {
		pushWrapper().clearAllNotifications()
		Donkey.giveUp(Donkey.TOPIC.ORDERS)

		//this catch blocks the function to throw up, effectively allowing the donkey to setup, even if the push unregister
		// fails........ this might cause double notifications, but on the other hand, if the call got processed but the
		// response was lost because of a flaky connection, we will still have donkey refreshes happening.
		API.deleteAndHandle('user/push', HTTP_CODE.UNAUTHORIZED__401)
			.catch(e => console.warn('DELETE user/push failed, but moving on...', e))

		pushWrapper.unregister()
		dispatch(slice.actions.setPush({ token: null, canReceive: false }))
	},

	/**
	 * Disables automatic push and enables interval-based refreshes.
	 * @param dispatch
	 * @returns {Promise<void>}
	 */
	pushOff: async dispatch => {
		dispatch(slice.thunks.clearPush)
		dispatch(slice.thunks.setupDonkeyRefreshes) //here it's going to enable the intervals
	},

	/** Creates or disables "is there a new order yet?" requests every minute. */
	setupDonkeyRefreshes: async (dispatch, getState) => {
		if (prefersPush(getState())) {
			if (Donkey.alreadyAskingAbout(Donkey.TOPIC.ORDERS)) {
				console.debug('Stop asking for orders, Donkey! We got push :)')
				Donkey.giveUp(Donkey.TOPIC.ORDERS)
			}
			return
		}

		Donkey.askAbout(Donkey.TOPIC.ORDERS, Donkey.ORDERS_INTERVAL, getState, async currentState => {
			const oldOrders  = getOrderIds(currentState)
			const lastUpdate = getUpdatedAt(currentState)

			await dispatch(fetchOrders)

			const now = newDate(null)
			if (!lastUpdate || differenceInMinutes(now, lastUpdate) > 30) {
				console.debug("Maybe, but we don't really care anymore ¯\\_(ツ)_/¯")
				return //we never got orders, or got a while ago, so there's no need to notify about what we got
			}

			const newState  = getState()
			const newOrders = difference(getOrderIds(newState), oldOrders)
			if (newOrders.length) {
				console.debug('Yeah, Donkey!!!', newOrders)
				newOrders
					.map(id => Order.fromState(id)(newState)[0])
					.filter(order => differenceInMinutes(order.ready_at, now) > -120) //skip what should've been notified 2h ago
					.forEach(order => notifyOrder(order)) // forEach(notifyOrder) would mix the optional notifyOrder's args
			}
		})
	},
}

/**
 * There are two platforms where push won't work:
 * - web, because it needs special treatment and implementation - see sc-10589
 * - iOS simulators; here, we have two different problems:
 *   1. push simply doesn't work as expected, since it's not connected to Apple servers, and you need a real .apns file to drag onto the simulator to make the push "happen". Thus, no reason to allow registering in the first place.
 *   2. besides that, push registering seems erratic from the Cordova POV - it doesn't seem to trigger the registration callback, so you're kinda left in a limbo ¯\_(ツ)_/¯
 * Nonetheless, you can override this boolean easily in this function to test out some behavior during development :)
 * @returns {boolean}
 */
export const isPushAllowed    = () => platform.isNative() && !platform.isIOSSimulator()
export const isPushReceivable = ({ push }) => push.canReceive
export const prefersPush      = state => isPushReceivable(state) && isPushAllowed()

export const { togglePush } = slice.actions

export const { initPush, pushOn, pushOff, clearPush, setupDonkeyRefreshes } = slice.thunks
export default slice.reducer
